import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MdContentCopy, MdShare } from "react-icons/md";
import { toast } from "react-toastify";
import { MdMarkEmailUnread } from "react-icons/md";
import ShareModal from "../components/modal/ShareModal";
import { getOemPartnerRequest } from "../redux/actions/oemActions";
import {
  getDashboardStatsRequest,
  getMonthlyScanStatsRequest,
} from "../redux/actions/dashboardAction/dashboardAction";
import "../styles/Dashboard.css";
import EmailModal from "../components/modal/EmailModal";
import ThreatChart from "../components/ThreatChart";
import RecentActivity from "../components/RecentActivity";
import {
  MdPeople,
  MdSearch,
  MdAttachMoney,
  MdCurrencyExchange,
  MdOutlineQrCodeScanner
} from "react-icons/md";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../styles/custom-datepicker.css";
import LoadingSpinner from "../components/ui/LoadingSpinner";

const Dashboard = () => {
  const [activeCard, setActiveCard] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const id = user?.oemPartner?.id;
  const { data: oemPartner, loading: oemLoading } = useSelector(
    (state) => state.oem
  );
  const {
    stats: dashboardStats,
    loading: statsLoading,
    monthlyStats,
    monthlyLoading,
  } = useSelector((state) => state.dashboard);
  const shareUrl = oemPartner?.oemUrl;
  const scanType = user?.oemPartner?.scanType;
  useEffect(() => {
    if (scanType === "Multiple") {
      navigate("/scan");
      return;
    }
    
    if (id && user) {
      dispatch(getOemPartnerRequest(id));
 
      if (selectedMonth) {
        const monthNumber = selectedMonth.getMonth() + 1;
        const yearNumber = selectedMonth.getFullYear();
        dispatch(getDashboardStatsRequest(id, monthNumber));
        dispatch(getMonthlyScanStatsRequest(id, yearNumber));
      }
    }
  }, [dispatch, id, selectedMonth, user, scanType, navigate]);

  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const handleMonthChange = (date) => {
    if (!date) return;
    setSelectedMonth(date);
    const monthNumber = date.getMonth() + 1;
    const yearNumber = date.getFullYear();
    dispatch(getDashboardStatsRequest(id, monthNumber));
    dispatch(getMonthlyScanStatsRequest(id, yearNumber));
  };

  const getCurrencyIcon = (currency) => {
    const currencyIcons = {
      usd: MdAttachMoney,
      hkd: MdCurrencyExchange,
      sgd: MdCurrencyExchange,
    };
    return currencyIcons[currency.toLowerCase()] || MdCurrencyExchange;
  };

  const getCurrencyColor = (currency, index) => {
    const colors = [
      "#f59e0b",
      "#8b5cf6",
      "#06b6d4",
      "#f97316",
      "#84cc16",
    ];
    const currencyColors = {
      usd: "#f59e0b",
      hkd: "#8b5cf6",
      sgd: "#06b6d4",
    };
    return (
      currencyColors[currency.toLowerCase()] || colors[index % colors.length]
    );
  };

  const getDashboardStats = () => {
    const baseStats = [
      {
        title: "End Users",
        value: dashboardStats?.endUserCount || "--",
        icon: MdPeople,
        color: "#3b82f6",
      },
      {
        title: "Total Scans",
        value: dashboardStats?.scanCount || "--",
        icon: MdSearch,
        color: "#10b981",
      },
        {
        title: "My Domain Scan Count",
        value: dashboardStats?.oemMyScanCount || "--",
        icon: MdOutlineQrCodeScanner,
        color: "red",
      },
    ];

    if (!dashboardStats?.revenue) {
      return [
        ...baseStats,
        {
          title: "Revenue (USD)",
          value: "--",
          icon: MdAttachMoney,
          color: "#f59e0b",
        },
      ];
    }

    // Dynamically create revenue cards for all currencies
    const revenueStats = Object.entries(dashboardStats.revenue)
      .filter(([key]) => key !== "totalAmount" && key.toLowerCase() !== "yen") // Exclude totalAmount and yen
      .map(([currency, amount], index) => ({
        title: `Revenue (${currency.toUpperCase()})`,
        value: `${amount || "0"}`,
        icon: getCurrencyIcon(currency),
        color: getCurrencyColor(currency, index),
      }));

    return [...baseStats, ...revenueStats];
  };
  const handleCopyLink = () => {
    try {
      if (!shareUrl) {
        toast.error("No link available to copy");
        return;
      }
      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      textArea.style.position = "fixed"; // avoid scroll
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);

      toast.success("Link copied to clipboard!");
    } catch (error) {
      console.error("Copy failed:", error);
      toast.error("Failed to copy link");
    }
  };
  const handleSendMail = () => {
    setShowEmailModal(true);
  };

  const handleCopyLicenseKey = () => {
    try {
      const licenseKey = user?.oemPartner?.licenseKey;
      if (!licenseKey) {
        toast.error("No license key available to copy");
        return;
      }
      const textArea = document.createElement("textarea");
      textArea.value = licenseKey;
      textArea.style.position = "fixed";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);

      toast.success("License key copied to clipboard!");
    } catch (error) {
      console.error("Copy failed:", error);
      toast.error("Failed to copy license key");
    }
  };

  // Show loading spinner when initial data is loading or when OEM partner data is not available
  if (oemLoading || (!oemPartner && id) || (statsLoading && !dashboardStats)) {
    return (
      <LoadingSpinner
        translations={{ verifyingToken: "Loading dashboard..." }}
      />
    );
  }

  // If no user ID is available, show error state
  if (!id) {
    return (
      <div className="page-container">
        <div className="page-card scroll-container">
          <div className="dashboard" style={{ textAlign: "center", padding: "100px 20px" }}>
            <h2 style={{ color: "#ef4444", marginBottom: "20px" }}>Error</h2>
            <p style={{ color: "#7f8c8d", fontSize: "18px" }}>
              Unable to load dashboard. Please try refreshing the page.
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      <div className="page-container">
        <div className="page-card scroll-container">
          <div className="dashboard">
            <div className="dashboard-header">
              <div>
                <h4>
                  Welcome{" "}
                  {oemPartner?.firstName
                    ? oemPartner.firstName.charAt(0).toUpperCase() +
                      oemPartner.firstName.slice(1)
                    : ""}
                </h4>
                <p>
                  {scanType === "License"
                    ? "This is your unique URL and License Key. Share them with your users to let them scan their domains"
                    : "This is your unique URL. Share it with your users to let them scan their domains"}
                </p>
              </div>
            </div>

            <div className="link-section">
              <div className="link-container">
                <div className="link-text">
                  <span className="shareUrl">{shareUrl}</span>
                </div>
                <div className="link-actions">
                  <button
                    className="copy-btn"
                    title="Copy link"
                    onClick={handleCopyLink}
                  >
                    <MdContentCopy />
                  </button>
                  <button
                    className="copy-btn"
                    title="Send in mail"
                    onClick={handleSendMail}
                  >
                    <MdMarkEmailUnread />
                  </button>
                  <button
                    className="share-btn-dashboard"
                    title="Send link"
                    onClick={() => setShowShareModal(true)}
                  >
                    <MdShare />
                  </button>
                </div>
              </div>
            </div>

            {scanType === "License" && user?.oemPartner?.licenseKey && (
              <div className="link-section mt-3">
                <div className="link-container">
                  <div className="link-text">
                    <span className="shareUrl">
                      {user.oemPartner.licenseKey}
                    </span>
                  </div>
                  <div className="link-actions">
                    <button
                      className="share-btn-dashboard"
                      title="Copy license key"
                      onClick={handleCopyLicenseKey}
                    >
                      <MdContentCopy />
                    </button>
                    {/* <button
                      className="share-btn-dashboard"
                      title="Share license key"
                      onClick={() => {
                        const licenseKey = user?.oemPartner?.licenseKey;
                        if (licenseKey) {
                          setShowShareModal(true);
                        }
                      }}
                    >
                      <MdShare />
                    </button> */}
                  </div>
                </div>
                <div className="license-key-label mt-2">
                  <small style={{ color: "#6b7280", fontSize: "12px" }}>
                    📄 License Key - Share this key with your users for
                    license-based scanning
                  </small>
                </div>
              </div>
            )}
            <div className="month-filter-container mb-4">
              <div
                className="filter-wrapper d-flex align-items-center gap-3 p-3"
                style={{
                  background:
                    "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                }}
              >
                <div
                  className="filter-icon"
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "18px",
                  }}
                >
                  📅
                </div>
                <div className="filter-content flex-grow-1">
                  <label
                    htmlFor="month"
                    className="filter-label mb-1 d-block"
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      margin: "0",
                    }}
                  >
                    Filter by Month
                  </label>
                  <DatePicker
                    selected={selectedMonth}
                    onChange={handleMonthChange}
                    dateFormat="MMMM yyyy"
                    showMonthYearPicker
                    className="custom-date-picker"
                    popperProps={{
                      strategy: "fixed",
                      modifiers: [
                        {
                          name: "preventOverflow",
                          options: {
                            boundary: "viewport",
                          },
                        },
                      ],
                    }}
                    style={{
                      padding: "10px 16px",
                      borderRadius: "10px",
                      border: "2px solid #e5e7eb",
                      fontSize: "14px",
                      fontWeight: "500",
                      backgroundColor: "white",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      outline: "none",
                      minWidth: "180px",
                      background:
                        "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                      color: "#374151",
                    }}
                  />
                </div>
              
              </div>
            </div>
            <div className="row mb-1">
              {statsLoading ? (
                <div className="col-12 text-center">
                  <div className="custom-spinner"></div>
                  <p className="mt-3 loading-text">
                    Loading dashboard stats...
                  </p>
                </div>
              ) : (
                getDashboardStats().map((stat, index) => (
                  <div key={index} className="col-lg-3 col-md-6 mb-4">
                    <div
                      className={`dashboard-stat-card ${
                        activeCard === index ? "active" : ""
                      } ${statsLoading ? "loading" : ""}`}
                      onClick={() =>
                        setActiveCard(activeCard === index ? null : index)
                      }
                    >
                      <div className="stat-card-inner d-flex align-items-center gap-3">
                        <div className="stat-icon-wrapper">
                          <div
                            className="stat-icon-circle"
                            style={{
                              backgroundColor: `${stat.color}15`,
                              borderColor: stat.color,
                              color: stat.color,
                            }}
                          >
                            <stat.icon className="stat-icons-main" />
                          </div>
                          <div
                            className="stat-glow"
                            style={{ backgroundColor: stat.color }}
                          ></div>
                        </div>
                        <div className="stat-content">
                          <h3 className="stat-value mb-0">
                            {stat.value}
                            <span
                              className="stat-title mb-0"
                              style={{
                                marginLeft: "8px",
                                fontSize: "14px",
                                fontWeight: "normal",
                              }}
                            >
                              {stat.title}
                            </span>
                          </h3>
                        </div>
                        <div
                          className="stat-overlay"
                          style={{
                            background: `linear-gradient(135deg, ${stat.color}10, ${stat.color}05)`,
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="row mb-4">
              <div className="col-lg-8">
                <ThreatChart data={monthlyStats} loading={monthlyLoading} year={selectedMonth.getFullYear()} />
              </div>
              <div className="col-lg-4">
                <div className="vulnerability-widget">
                  <div className="widget-header d-flex align-items-center justify-content-between mb-3">
                    <h6 className="mb-0">👥 User Verification Status</h6>
                  </div>
                  <div className="user-status-list">
                    <div className="user-status-item verified mb-3">
                      <div className="status-header d-flex justify-content-between align-items-center">
                        <span className="status-label fw-bold">
                          Verified Users
                        </span>
                        <span className="status-count badge bg-success">
                          {dashboardStats?.verifiedUserCount || 0}
                        </span>
                      </div>
                      <div className="status-details mt-1">
                        <span className="status-description text-muted small">
                          Users with verified accounts
                        </span>
                      </div>
                    </div>
                    <div className="user-status-item unverified mb-3">
                      <div className="status-header d-flex justify-content-between align-items-center">
                        <span className="status-label fw-bold">
                          Unverified Users
                        </span>
                        <span className="status-count badge bg-warning">
                          {dashboardStats?.unverifiedUserCount || 0}
                        </span>
                      </div>
                      <div className="status-details mt-1">
                        <span className="status-description text-muted small">
                          Users pending verification
                        </span>
                      </div>
                    </div>
                    <div className="user-status-item total mb-3">
                      <div className="status-header d-flex justify-content-between align-items-center">
                        <span className="status-label fw-bold">
                          Total Users
                        </span>
                        <span className="status-count badge bg-primary">
                          {(dashboardStats?.verifiedUserCount || 0) +
                            (dashboardStats?.unverifiedUserCount || 0)}
                        </span>
                      </div>
                      <div className="status-details mt-1">
                        <span className="status-description text-muted small">
                          All registered users
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {scanType !== "License" && (
              <div className="row">
                <div className="col-lg-12">
                  <RecentActivity
                    recentTransactions={
                      dashboardStats?.recentTransactions || []
                    }
                    recentScans={dashboardStats?.recentScans || []}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        shareUrl={shareUrl}
        companyData={oemPartner}
        licenseKey={
          scanType === "License" ? user?.oemPartner?.licenseKey : null
        }
      />

      <EmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        oemId={id}
        shareUrl={shareUrl}
        shareLicenseKey={
          scanType === "License" ? user?.oemPartner?.licenseKey : null
        }
      />
    </>
  );
};

export default Dashboard;
